import React, { useState, useEffect, useCallback } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalGraduates: 0,
    totalDepartments: 0,
    totalCourses: 0,
    totalExamBodies: 0,
    pendingGownRequests: 0,
    gradeDistribution: {
      Distinction: 0,
      Credit: 0,
      Pass: 0,
      Mastery: 0,
      Proficiency: 0,
      Competent: 0,
      FirstClass: 0
    }
  });
  const [departmentsStats, setDepartmentsStats] = useState([]);
  const [levelStats, setLevelStats] = useState([]);
  const [examBodyStats, setExamBodyStats] = useState([]);
  const [recentGraduates, setRecentGraduates] = useState([]);
  const [loading, setLoading] = useState(true);

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'Distinction': return 'bg-yellow-500';
      case 'Credit': return 'bg-blue-500';
      case 'Pass': return 'bg-green-500';
      case 'Mastery': return 'bg-purple-500';
      case 'Proficiency': return 'bg-indigo-500';
      case 'Competent': return 'bg-cyan-500';
      case 'First Class': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [graduatesRes, departmentsRes, coursesRes, examBodiesRes, gownsRes] = await Promise.all([
        api.get('/graduates'),
        api.get('/departments'),
        api.get('/courses'),
        api.get('/exam-bodies'),
        api.get('/gowns/requests').catch(() => ({ data: [] }))
      ]);

      const graduates = graduatesRes.data;
      const departments = departmentsRes.data;
      const courses = coursesRes.data;
      const examBodies = examBodiesRes.data;
      const gownRequests = gownsRes.data || [];

      let filteredGraduates = graduates;
      if (user?.role === 'editor' && user?.department_id) {
        filteredGraduates = graduates.filter(g => g.department_id === parseInt(user.department_id));
      }

      // Calculate grade distribution
      const gradeDist = {
        Distinction: 0,
        Credit: 0,
        Pass: 0,
        Mastery: 0,
        Proficiency: 0,
        Competent: 0,
        FirstClass: 0
      };
      
      filteredGraduates.forEach(grad => {
        if (grad.grade && gradeDist.hasOwnProperty(grad.grade)) {
          gradeDist[grad.grade]++;
        }
      });

      // Department statistics
      const deptStats = departments.map(dept => {
        const deptGrads = filteredGraduates.filter(g => g.department_id === dept.id);
        const gradeBreakdown = {
          Distinction: deptGrads.filter(g => g.grade === 'Distinction').length,
          Credit: deptGrads.filter(g => g.grade === 'Credit').length,
          Pass: deptGrads.filter(g => g.grade === 'Pass').length,
          Mastery: deptGrads.filter(g => g.grade === 'Mastery').length,
          Proficiency: deptGrads.filter(g => g.grade === 'Proficiency').length,
          Competent: deptGrads.filter(g => g.grade === 'Competent').length,
          FirstClass: deptGrads.filter(g => g.grade === 'First Class').length
        };
        const totalPassing = Object.values(gradeBreakdown).reduce((a, b) => a + b, 0);
        
        return {
          id: dept.id,
          name: dept.name,
          total: deptGrads.length,
          gradeBreakdown,
          successRate: deptGrads.length ? Math.round((totalPassing / deptGrads.length) * 100) : 0
        };
      }).filter(d => d.total > 0 || user?.role === 'admin');

      // Level statistics from course_levels
      const levelData = {};
      filteredGraduates.forEach(grad => {
        const levelDisplay = grad.level_number ? `Level ${grad.level_number}` : grad.level_name;
        if (levelDisplay && levelDisplay !== '—') {
          if (!levelData[levelDisplay]) {
            levelData[levelDisplay] = { name: levelDisplay, count: 0, level_number: grad.level_number };
          }
          levelData[levelDisplay].count++;
        }
      });
      const levelStatsArray = Object.values(levelData).sort((a, b) => {
        if (a.level_number && b.level_number) return b.level_number - a.level_number;
        if (a.level_number) return -1;
        if (b.level_number) return 1;
        return a.name.localeCompare(b.name);
      });

      // Exam body statistics
      const examStats = examBodies.map(exam => {
        const examGrads = filteredGraduates.filter(g => g.exam_body_id === exam.id);
        return {
          id: exam.id,
          name: exam.name,
          code: exam.code,
          total: examGrads.length
        };
      }).filter(e => e.total > 0);

      // Recent graduates
      const recent = [...filteredGraduates]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setStats({
        totalGraduates: filteredGraduates.length,
        totalDepartments: departments.length,
        totalCourses: courses.length,
        totalExamBodies: examBodies.length,
        pendingGownRequests: gownRequests.filter(g => g.status === 'pending').length,
        gradeDistribution: gradeDist
      });
      setDepartmentsStats(deptStats);
      setLevelStats(levelStatsArray);
      setExamBodyStats(examStats);
      setRecentGraduates(recent);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getGradeDisplayName = (grade) => {
    switch(grade) {
      case 'Distinction': return 'Distinction (KNEC)';
      case 'Credit': return 'Credit (KNEC)';
      case 'Pass': return 'Pass (KNEC/NITA)';
      case 'Mastery': return 'Mastery (TVET-CDACC)';
      case 'Proficiency': return 'Proficiency (TVET-CDACC)';
      case 'Competent': return 'Competent (TVET-CDACC)';
      case 'FirstClass': return 'First Class (NITA)';
      default: return grade;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back, {user?.name || 'Admin'}!</p>
      </div>

      {/* Key Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <Card>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Total Graduates</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalGraduates}</p>
            </div>
            <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
              <span className="text-2xl">👨‍🎓</span>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Departments</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalDepartments}</p>
            </div>
            <div className="w-12 h-12 bg-secondary bg-opacity-10 rounded-full flex items-center justify-center">
              <span className="text-2xl">🏛️</span>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Courses</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalCourses}</p>
            </div>
            <div className="w-12 h-12 bg-accent bg-opacity-10 rounded-full flex items-center justify-center">
              <span className="text-2xl">📚</span>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Exam Bodies</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalExamBodies}</p>
            </div>
            <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
              <span className="text-2xl">📝</span>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Pending Gown Requests</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.pendingGownRequests}</p>
            </div>
            <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
              <span className="text-2xl">👗</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Grade Distribution */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Grade Distribution by Exam Body</h2>
          <div className="space-y-4">
            {Object.entries(stats.gradeDistribution).filter(([_, count]) => count > 0).map(([grade, count]) => (
              <div key={grade}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{getGradeDisplayName(grade)}</span>
                  <span className="font-semibold" style={{ color: getGradeColor(grade).replace('bg-', 'text-') }}>
                    {count}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${getGradeColor(grade)} h-2 rounded-full`}
                    style={{ width: `${stats.totalGraduates ? (count / stats.totalGraduates) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
            {Object.values(stats.gradeDistribution).every(v => v === 0) && (
              <p className="text-gray-500 text-center py-4">No grade data available</p>
            )}
          </div>
        </Card>

        {/* Level Distribution */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Graduates by Level</h2>
          {levelStats.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No data available</p>
          ) : (
            <div className="space-y-3">
              {levelStats.map(level => (
                <div key={level.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{level.name}</span>
                    <span className="font-semibold text-primary">{level.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full"
                      style={{ width: `${stats.totalGraduates ? (level.count / stats.totalGraduates) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Department Statistics */}
      <Card className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Department Statistics</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Graduates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distinction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pass</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mastery</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proficiency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Class</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
               </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departmentsStats.length === 0 ? (
                 <tr>
                  <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                    No department data available
                   </td>
                 </tr>
              ) : (
                departmentsStats.map(dept => (
                  <tr key={dept.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {dept.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {dept.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">
                      {dept.gradeBreakdown.Distinction}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {dept.gradeBreakdown.Credit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {dept.gradeBreakdown.Pass}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                      {dept.gradeBreakdown.Mastery}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">
                      {dept.gradeBreakdown.Proficiency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-cyan-600">
                      {dept.gradeBreakdown.Competent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-600">
                      {dept.gradeBreakdown.FirstClass}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                      {dept.successRate}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Exam Body Statistics */}
      {examBodyStats.length > 0 && (
        <Card className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Graduates by Exam Body</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {examBodyStats.map(exam => (
              <div key={exam.id} className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-lg font-semibold text-primary">{exam.name}</div>
                <div className="text-2xl font-bold text-gray-800 mt-2">{exam.total}</div>
                <div className="text-sm text-gray-500">Graduates</div>
                <div className="text-xs text-gray-400 mt-1">Code: {exam.code}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Button variant="primary" className="w-full justify-center" onClick={() => window.location.href = '/admin/graduates'}>
              + Add New Graduate
            </Button>
            <Button variant="secondary" className="w-full justify-center" onClick={() => window.location.href = '/admin/reports'}>
              📊 Generate Reports
            </Button>
            <Button variant="outline" className="w-full justify-center" onClick={() => window.location.href = '/admin/graduation-days'}>
              🎓 Manage Graduation Days
            </Button>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Graduates</h2>
          {recentGraduates.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No recent graduates</p>
          ) : (
            <div className="space-y-3">
              {recentGraduates.map(grad => (
                <div key={grad.id} className="flex justify-between items-center border-b pb-2">
                  <div>
                    <p className="font-medium text-gray-900">{grad.name}</p>
                    <p className="text-sm text-gray-500">{grad.admin_number} • {grad.department_name}</p>
                    <p className="text-xs text-gray-400">{grad.course_name}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded ${
                      grad.grade === 'Distinction' ? 'bg-yellow-100 text-yellow-800' :
                      grad.grade === 'Credit' ? 'bg-blue-100 text-blue-800' :
                      grad.grade === 'Pass' ? 'bg-green-100 text-green-800' :
                      grad.grade === 'Mastery' ? 'bg-purple-100 text-purple-800' :
                      grad.grade === 'Proficiency' ? 'bg-indigo-100 text-indigo-800' :
                      grad.grade === 'Competent' ? 'bg-cyan-100 text-cyan-800' :
                      grad.grade === 'First Class' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {grad.grade || 'N/A'}
                    </span>
                    <div className="text-xs text-gray-400 mt-1">
                      {grad.ceremony_date ? new Date(grad.ceremony_date).toLocaleDateString() : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
